import { useState, useEffect, useMemo } from "react";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import "./Hero.css";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";
import translations from "../translations";
import logoImage from "../assets/vlogo.png";
import netscoutLogo from '../assets/netscout.svg';

const Hero = () => {
  const { lang } = useLanguage();
  const t = translations[lang];
  const isRTL = lang === 'ar';
  const phrases = useMemo(() => t.hero_phrases, [t]);

  const [text, setText] = useState("");
  const [phraseIndex, setPhraseIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [cursorVisible, setCursorVisible] = useState(true);
  const [isTechResourcesLogo, setIsTechResourcesLogo] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    setText("");
    setPhraseIndex(0);
    setIsDeleting(false);
  }, [lang]);

  useEffect(() => {
    const backSpeed = 50;
    const typeSpeed = isDeleting ? backSpeed : 150;

    const cursorTimeout = setTimeout(() => setCursorVisible((v) => !v), 500);

    if (!isDeleting && text === phrases[phraseIndex]) {
      setTimeout(() => setIsDeleting(true), 1000);
    } else if (isDeleting && text === "") {
      setIsDeleting(false);
      setPhraseIndex((prev) => (prev + 1) % phrases.length);
    }

    const timeout = setTimeout(() => {
      setText((cur) =>
        isDeleting
          ? cur.slice(0, cur.length - 1)
          : cur + phrases[phraseIndex].charAt(cur.length)
      );
    }, typeSpeed);

    return () => { clearTimeout(timeout); clearTimeout(cursorTimeout); };
  }, [text, isDeleting, phrases, phraseIndex]);

  useEffect(() => {
    const id = setInterval(() => setIsTechResourcesLogo((p) => !p), 3000);
    return () => clearInterval(id);
  }, []);

  const logoVariants = {
    initial: { opacity: 0, x: -20 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.8 } },
    exit: { opacity: 0, x: 20, transition: { duration: 0.8 } },
  };

  return (
    <div className="relative text-white overflow-hidden" id="hero" dir={isRTL ? 'rtl' : 'ltr'}>
      <Helmet>
        <title>{isRTL ? 'Technology Resources — حماية البنية الرقمية للعراق' : "Technology Resources — Iraq's DDoS Protection Leader"}</title>
        <meta name="description" content={isRTL ? 'شركة Technology Resources بالشراكة مع NETSCOUT Arbor لتوفير حماية من هجمات DDoS للبنية التحتية الرقمية في العراق.' : "Technology Resources partners with NETSCOUT Arbor to deliver enterprise-grade anti-DDoS protection for Iraq's national digital infrastructure."} />
      </Helmet>
      <video className="absolute top-0 start-0 w-full h-full object-cover z-0" autoPlay muted loop>
        <source src="/assets/cyberzoom.mp4" type="video/mp4" />
      </video>
      <div className="absolute top-0 start-0 w-full h-full bg-black opacity-50"></div>
      <div className="relative max-w-[800px] mt-[-96px] w-full h-screen mx-auto text-center flex flex-col justify-center">
        <div className={`text-white ${isRTL ? 'font-arabic' : ''}`}>
          <motion.div
            className="logo-container"
            variants={logoVariants}
            initial="initial"
            animate="visible"
            exit="exit"
            key={isTechResourcesLogo ? "tech-resources-logo" : "netscout-logo"}
            style={{ display: "flex", justifyContent: "center" }}
          >
            {isTechResourcesLogo ? (
              <img src={logoImage} alt="Technology Resources Logo" className="logo" />
            ) : (
              <img src={netscoutLogo} alt="NETSCOUT Arbor Logo" className="logo" style={{ width: "80%", height: "80%" }} />
            )}
          </motion.div>
          <p className="text-[#97d700] font-magistral p-2">{t.hero_tagline}</p>
          <h1 className="md:text-7xl sm:text-6xl text-4xl font-bold font-magistral md:py-6">{t.hero_headline}</h1>
          <div className="flex justify-center items-center gap-2">
            <p className="md:text-5xl sm:text-4xl text-xl font-bold font-magistral py-4">{t.hero_leading}</p>
            <p className="md:text-5xl sm:text-4xl text-xl font-light font-magistral">
              {text}
              <span className={cursorVisible ? 'blinking-cursor' : ''}>|</span>
            </p>
          </div>
          <p className="md:text-2xl text-xl font-magistral text-gray-300 max-w-2xl mx-auto leading-[1.9]">{t.hero_subtext}</p>
          <button
            onClick={() => navigate("/about-us")}
            className="bg-[#97d700] w-[200px] rounded-lg font-magistral my-6 mx-auto px-6 py-3 text-black hover:bg-[#a8e600] transition-colors duration-200"
          >
            {t.hero_cta}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Hero;
